import React, { useContext, useEffect, useState } from "react";
import Breadcrumb from "../../../CommonComponents/BreadCrumb/BreadCrumb";
import CustomTable from "../../../CommonComponents/Table/CustomTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import DataPerPage from "../../../CommonComponents/Pagination/DataPerPage";
import SearchComponent from "../../../CommonComponents/SearchComponent/SearchComponent";
import Button from "../../../Elements/Button";
import { networkService } from "../../../config/axios";
import DeleteOrgModal from "../../../Modals/Admin/DeleteOrgModal";
import UserInfoContext from "../../../../utils/userInfoContext";
import AdminPagination from "../../../CommonComponents/Pagination/AdminPagination";
import {useLocation} from "react-router-dom";
import "../admin.css";

const AdminPotentialBotsPage = () => {
    const { setShowNotifications } = useContext(UserInfoContext)
    const [potentialId, setPotentialId] = useState(null)
    const { pathname } = useLocation();
    const [potentialBots, setPotentialBots] = useState([])
    const [currentPageData, setCurrentPageData] = useState([]);
    const [dataPerPage, setDataPerPage] = useState(10)
    const totalData = potentialBots?.length;
    const { userInfoData } = useContext(UserInfoContext);

    const isAdminRoute = pathname.startsWith('/admin');

    const isWaitingForData =
        !userInfoData || Object.keys(userInfoData).length === 0;

    const userType = userInfoData?.user_type;

    const shouldShowSuperAdminLinks =
        userType === 1 || (isWaitingForData && isAdminRoute);
    const refreshBots = async () => {
        const response = await networkService("admin/potential_bots.json", "GET");
        setPotentialBots(response.data?.potential_bots || []);
    };

    const handlePageChange = (pageNumber) => {
        const indexOfLastData = pageNumber * dataPerPage;
        const indexOfFirstData = indexOfLastData - dataPerPage;
        setCurrentPageData(potentialBots?.slice(indexOfFirstData, indexOfLastData));
    };

    const handleDeleteBot = async (id) => {
        const url = `admin/potential_bots/${id}.json`;

        try {
            const response = await networkService(url, "DELETE");

            if (response.status === 204) {
                await refreshBots();
                setPotentialId(null);
                setShowNotifications({
                    showToast: true,
                    toastType: "success",
                    toastMessage: "Potential bot deleted",
                });
            }
        } catch (error) {
            setPotentialId(null);
            setShowNotifications({
                showToast: true,
                toastType: "danger",
                toastMessage: "Something went wrong",
            });
        }
    };

    const handleUpdateBot = async (id) => {
        const url = `admin/potential_bots/${id}.json`;

        try {
            const response = await networkService(url, "PUT");

            if (response.status === 200) {
                await refreshBots();

                setShowNotifications({
                    showToast: true,
                    toastType: "success",
                    toastMessage: "Potential bot confirmed",
                });
            }
        } catch (error) {
            setShowNotifications({
                showToast: true,
                toastType: "danger",
                toastMessage: "Something went wrong",
            });
        }
    };

    useEffect(() => {
        const fetchBots = async () => {
            const response = await networkService("admin/potential_bots.json", "GET");
            setPotentialBots([...response.data?.potential_bots]);
        };
        fetchBots();
    }, [potentialId]);


    useEffect(() => {
        handlePageChange(1);
    }, [potentialBots, dataPerPage])


    const columns = React.useMemo(() => {
        const baseColumns = [
            {
                Header: 'IP Address',
                accessor: row => row?.ip_address,
                Cell: ({ value }) => (value ? value : 'No IP Address'),
            },
            {
                Header: 'Users',
                accessor: 'users',
                Cell: ({ row: { original } }) => {
                    const users = original?.users || [];
                    const names = users.map(user => user.name).join(', ');
                    return <>{names || 'User Name'}</>;
                },
            },
            {
                Header: 'Counts',
                accessor: row => row?.count
            },
            {
                Header: 'Status',
                accessor: 'status',
                Cell: ({ row }) => (row?.original?.status),
            },
            {
                Header: 'Confirmed by',
                accessor: 'confirmed_by',
                Cell: ({ row }) => (row?.original?.confirmed_by),
            },
            {
                Header: 'Confirm',
                accessor: 'confirm',
                Cell: ({ row }) => {
                    const { id, status } = row.original;

                    if (status === 'Confirmed') {
                        return (
                            <FontAwesomeIcon
                                icon={faCheckCircle}
                                className="text-success"
                                title="Already confirmed"
                            />
                        );
                    }

                    return (
                        <div
                            className="cursor-pointer text-success"
                            onClick={() => handleUpdateBot(id)}
                        >
                            Confirm
                        </div>
                    );
                },
            },
        ];

        if (shouldShowSuperAdminLinks) {
            baseColumns.push({
                Header: 'Actions',
                accessor: 'action',
                Cell: ({ row }) => (
                    <img
                        src="/icons/delete.svg"
                        alt=""
                        className="cancel cursor-pointer admin-bot-action-icon"
                        onClick={() => setPotentialId(row.original.id)}
                        role="button"
                    />
                ),
            });
        }

        return baseColumns;
    }, [currentPageData, shouldShowSuperAdminLinks]);

    return (
        <>
            <DeleteOrgModal
                title={'Delete Potential Bot'}
                openModal={potentialId !== null}
                closeModal={() => setPotentialId(null)}
                name={'delete Potential bot'}
                handleDelete={() => handleDeleteBot(potentialId)}
            />

            <div className="admin-module">
                <div className="admin-bot-page admin-content">
                    <Breadcrumb string="Admin / Potential Bots" />

                    <div className="container admin-bot-container">
                        <div className="show-select1 d-flex mb-4 admin-bot-header">
                            <div className="admin-bot-controls admin-bot-search-group">
                                {totalData > dataPerPage && <DataPerPage dataPerPage={dataPerPage} setDataPerPage={setDataPerPage} />}
                                <div className="admin-bot-search-input-wrap">
                                    <SearchComponent
                                        data={potentialBots}
                                        setCurrentPageData={setCurrentPageData}
                                        placeholder="Search here"
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="admin-bot-search-btn"
                                    aria-label="Search"
                                >
                                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                                </button>
                            </div>

                            <Button
                                classList="success"
                                addonClass="admin-bot-cta"
                                text="+ Add Bot"
                                onClick={() => window.location.href = "/admin/potential_bots/new"}
                                type="button"
                                aria-label="Add new bot"
                            />
                        </div>

                        <div className="top-parent admin-bot-table-wrapper">
                            <CustomTable data={currentPageData} columnData={columns} />
                        </div>

                        {totalData > dataPerPage && (
                            <AdminPagination
                                dataPerPage={dataPerPage}
                                totalData={totalData}
                                onPageChange={handlePageChange}
                                showRange={true}
                                previousLabel="‹ Previous"
                                nextLabel="Next ›"
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}
export default AdminPotentialBotsPage;
